import React, { useCallback, useEffect, useState } from 'react';
import { useLocalSearchParams } from 'expo-router';
import { BackHandler, Platform } from 'react-native';

import { GroupOrderTerminalStateModal } from '../../components/GroupOrderTerminalStateModal';
import { useGroupOrderPostOrderQuery } from '../../hooks/useGroupOrderingState/GraphQL/GroupOrderPostOrder.generated';
import {
  checkIfGroupOrderEndedMessageAllowed,
  storeDismissedGroupOrderId,
} from '../../hooks/useGroupOrderingState/group-order-reference-storage';
import { getTerminalGroupOrder } from '../../hooks/useGroupOrderingState/group-order-terminal-state';
import { useNavigateBack } from '../../navigation';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Tells a group order participant that the host submitted or ended the order.
 * It is a route so iOS can present it over native modals such as the Bag.
 */
export const GroupOrderEndedScreen = () => {
  const { orderId } = useLocalSearchParams<RouteParams>();

  const navigateBack = useNavigateBack();

  // ─── State ─────────────────────────────────────────────────────────────────

  // Read once, since dismissing the message rewrites the same stored reference.
  const [isMessageAllowed] = useState(() => checkIfGroupOrderEndedMessageAllowed(orderId));

  // ─── Query ─────────────────────────────────────────────────────────────────

  const [{ data, fetching }] = useGroupOrderPostOrderQuery({
    variables: { orderId },
    pause: !isMessageAllowed,
  });

  // ─── Derived ───────────────────────────────────────────────────────────────

  const terminalGroupOrder = getTerminalGroupOrder(data?.order);
  const shouldLeave = !isMessageAllowed || (!fetching && !terminalGroupOrder);
  const isMessageShown = isMessageAllowed && terminalGroupOrder !== undefined;

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const handleDismiss = useCallback(() => {
    storeDismissedGroupOrderId(orderId);
    navigateBack();
  }, [navigateBack, orderId]);

  const leaveWithoutTerminalGroupOrder = useCallback(() => {
    if (!shouldLeave) {
      return;
    }

    navigateBack();
  }, [navigateBack, shouldLeave]);

  const blockHardwareBackPress = useCallback(() => {
    if (Platform.OS !== 'android' || !isMessageShown) {
      return;
    }

    const subscription = BackHandler.addEventListener('hardwareBackPress', consumeBackPress);

    return () => {
      subscription.remove();
    };
  }, [isMessageShown]);

  // ─── Effects ───────────────────────────────────────────────────────────────

  useEffect(leaveWithoutTerminalGroupOrder, [leaveWithoutTerminalGroupOrder]);
  useEffect(blockHardwareBackPress, [blockHardwareBackPress]);

  // ───────────────────────────────────────────────────────────────────────────

  if (!isMessageAllowed || !terminalGroupOrder) {
    return null;
  }

  return (
    <GroupOrderTerminalStateModal
      hasCurrentUserItems={terminalGroupOrder.hasCurrentUserItems}
      hostDisplayName={terminalGroupOrder.hostDisplayName}
      orderChannel={terminalGroupOrder.orderChannel}
      orderState={terminalGroupOrder.orderState}
      onDismiss={handleDismiss}
    />
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function consumeBackPress() {
  return true;
}

// ─── Types ───────────────────────────────────────────────────────────────────

type RouteParams = {
  orderId: string;
};
